import {
  AfterContentInit,
  Directive,
  ElementRef,
  Injectable,
  Input,
  NgZone,
  OnDestroy,
} from '@angular/core';

import { InteractivityChecker } from './interactivity-checker';

/**
 * Class that allows for trapping focus within a DOM element.
 *
 * NOTE: This class currently uses a very simple (naive) approach to focus trapping.
 * It assumes that the tab order is the same as DOM order, which is not necessarily true.
 * Things like tabIndex > 0, flex `order`, and shadow roots can cause to two to misalign.
 * This will be replaced with a more intelligent solution before the library is considered stable.
 */
export class FocusTrap {
  private _startAnchor: HTMLElement | null;
  private _endAnchor: HTMLElement | null;

  /** Whether the focus trap is active. */
  public get enabled(): boolean {
    return this._enabled;
  }
  public set enabled(val: boolean) {
    this._enabled = val;

    if (this._startAnchor && this._endAnchor) {
      this._startAnchor.tabIndex = this._endAnchor.tabIndex = this._enabled ? 0 : -1;
    }
  }
  private _enabled: boolean = true;

  constructor(
    private _element: HTMLElement,
    private _checker: InteractivityChecker,
    private _ngZone: NgZone,
    deferAnchors: boolean = false,
  ) {
    if (!deferAnchors) {
      this.attachAnchors();
    }
  }

  /** Destroys the focus trap by cleaning up the anchors. */
  public destroy(): void {
    if (this._startAnchor && this._startAnchor.parentNode) {
      this._startAnchor.parentNode.removeChild(this._startAnchor);
    }

    if (this._endAnchor && this._endAnchor.parentNode) {
      this._endAnchor.parentNode.removeChild(this._endAnchor);
    }

    this._startAnchor = this._endAnchor = null;
  }

  /**
   * Inserts the anchors into the DOM. This is usually done automatically
   * in the constructor, but can be deferred for cases like directives with `*ngIf`.
   */
  public attachAnchors(): void {
    if (!this._startAnchor) {
      this._startAnchor = this._createAnchor();
    }

    if (!this._endAnchor) {
      this._endAnchor = this._createAnchor();
    }

    this._ngZone.runOutsideAngular(() => {
      this._startAnchor!.addEventListener('focus', () => this.focusLastTabbableElement());
      this._endAnchor!.addEventListener('focus', () => this.focusFirstTabbableElement());

      this._element.parentNode!.insertBefore(this._startAnchor!, this._element);
      this._element.parentNode!.insertBefore(this._endAnchor!, this._element.nextSibling);
    });
  }

  /** Focuses the first tabbable element within the focus trap region. */
  public focusFirstTabbableElement(): void {
    const redirectToElement =
      <HTMLElement>this._element.querySelector('[rt-focus-start]') ||
      this._getFirstTabbableElement(this._element);

    if (redirectToElement) {
      redirectToElement.focus();
    }
  }

  /** Focuses the last tabbable element within the focus trap region. */
  public focusLastTabbableElement(): void {
    const focusTargets = this._element.querySelectorAll('[rt-focus-end]');
    let redirectToElement: HTMLElement | null = null;

    if (focusTargets.length) {
      redirectToElement = <HTMLElement>focusTargets[focusTargets.length - 1];
    } else {
      redirectToElement = this._getLastTabbableElement(this._element);
    }

    if (redirectToElement) {
      redirectToElement.focus();
    }
  }

  /** Get the first tabbable element from a DOM subtree (inclusive). */
  private _getFirstTabbableElement(root: HTMLElement): HTMLElement | null {
    if (this._checker.isFocusable(root) && this._checker.isTabbable(root)) {
      return root;
    }

    // Iterate in DOM order. Note that IE doesn't have `children` for SVG so we fall
    // back to `childNodes` which includes text nodes, comments etc.
    const children = root.children || root.childNodes;

    for (let i = 0; i < children.length; i++) {
      const tabbableChild =
        children[i].nodeType === Node.ELEMENT_NODE
          ? this._getFirstTabbableElement(<HTMLElement>children[i])
          : null;

      if (tabbableChild) {
        return tabbableChild;
      }
    }

    return null;
  }

  /** Get the last tabbable element from a DOM subtree (inclusive). */
  private _getLastTabbableElement(root: HTMLElement): HTMLElement | null {
    if (this._checker.isFocusable(root) && this._checker.isTabbable(root)) {
      return root;
    }

    // Iterate in reverse DOM order.
    const children = root.children || root.childNodes;

    for (let i = children.length - 1; i >= 0; i--) {
      const tabbableChild =
        children[i].nodeType === Node.ELEMENT_NODE
          ? this._getLastTabbableElement(<HTMLElement>children[i])
          : null;

      if (tabbableChild) {
        return tabbableChild;
      }
    }

    return null;
  }

  /** Creates an anchor element. */
  private _createAnchor(): HTMLElement {
    const anchor = document.createElement('div');
    anchor.tabIndex = this._enabled ? 0 : -1;
    anchor.classList.add('rt-visually-hidden');
    anchor.classList.add('rt-focus-trap-anchor');
    return anchor;
  }
}

/** Factory that allows easy instantiation of focus traps. */
@Injectable({ providedIn: 'root' })
export class FocusTrapFactory {
  constructor(private _checker: InteractivityChecker, private _ngZone: NgZone) {}

  public create(element: HTMLElement, deferAnchors: boolean = false): FocusTrap {
    return new FocusTrap(element, this._checker, this._ngZone, deferAnchors);
  }
}

/**
 * Directive for trapping focus within a region.
 * @deprecated
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'rt-focus-trap',
})
export class FocusTrapDeprecatedDirective implements OnDestroy, AfterContentInit {
  private focusTrap: FocusTrap;

  /** Whether the focus trap is active. */
  @Input()
  public get disabled(): boolean {
    return !this.focusTrap.enabled;
  }
  public set disabled(val: boolean) {
    this.focusTrap.enabled = !val;
  }

  constructor(private _elementRef: ElementRef, private _focusTrapFactory: FocusTrapFactory) {
    this.focusTrap = this._focusTrapFactory.create(this._elementRef.nativeElement, true);
  }

  public ngOnDestroy(): void {
    this.focusTrap.destroy();
  }

  public ngAfterContentInit(): void {
    this.focusTrap.attachAnchors();
  }
}

/** Directive for trapping focus within a region. */
@Directive({
  selector: '[rtTrapFocus]',
  exportAs: 'rtTrapFocus',
})
export class FocusTrapDirective implements OnDestroy, AfterContentInit {
  private focusTrap: FocusTrap;

  /** Whether the focus trap is active. */
  @Input('rtTrapFocus')
  public get enabled(): boolean {
    return this.focusTrap.enabled;
  }
  public set enabled(value: boolean) {
    this.focusTrap.enabled = !!value;
  }

  constructor(private _elementRef: ElementRef, private _focusTrapFactory: FocusTrapFactory) {
    this.focusTrap = this._focusTrapFactory.create(this._elementRef.nativeElement, true);
  }

  public ngOnDestroy(): void {
    this.focusTrap.destroy();
  }

  public ngAfterContentInit(): void {
    this.focusTrap.attachAnchors();
  }
}
